/**
 * Timezone utility functions for converting and displaying event times
 */

/**
 * Get the user's current timezone
 * @returns {string} IANA timezone string (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format time from 24-hour to 12-hour AM/PM format
 * @param {string} time24 - Time in HH:MM format (24-hour)
 * @returns {string} Time in 12-hour AM/PM format (e.g., "3:00 PM")
 */
export const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return time24;
  }
};

/**
 * Get timezone abbreviation (e.g., "EST", "PST")
 * @param {string} timezone - IANA timezone string
 * @param {Date} date - Date to get abbreviation for (handles DST)
 * @returns {string} Timezone abbreviation
 */
export const getTimezoneAbbreviation = (timezone, date = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : timezone;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
};

/**
 * Convert event date/time from event timezone to user timezone
 * @param {string} eventDate - Date in YYYY-MM-DD format
 * @param {string} eventTime - Time in HH:MM format (24-hour)
 * @param {string} eventTimezone - IANA timezone of the event
 * @param {string} userTimezone - IANA timezone of the user
 * @returns {Object} { date: Date object, displayTime: string, eventTimeDisplay: string }
 */
export const convertEventTime = (eventDate, eventTime, eventTimezone, userTimezone = getUserTimezone()) => {
  try {
    // Parse the event date and time
    const [year, month, day] = eventDate.split('-').map(Number);
    const [hours, minutes] = eventTime.split(':').map(Number);
    
    // Create a date string that represents the event in its local timezone
    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    
    // Parse as a date in the event's timezone by using toLocaleString trick
    // Create date assuming it's in event timezone
    const tempDate = new Date(dateString);
    
    // Get what this date would be formatted as in the event timezone
    const eventTZString = tempDate.toLocaleString('en-US', { timeZone: eventTimezone });
    const eventTZDate = new Date(eventTZString);
    
    // Calculate the offset and create the correct UTC date
    const offset = tempDate.getTime() - eventTZDate.getTime();
    const correctDate = new Date(tempDate.getTime() + offset);
    
    // Format in user timezone with 12-hour format
    const userTimeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    // Format in event timezone with 12-hour format
    const eventTimeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    const userTime = userTimeFormatter.format(correctDate);
    const eventTimeStr = eventTimeFormatter.format(correctDate);
    const eventTZAbbr = getTimezoneAbbreviation(eventTimezone, correctDate);
    
    // If user is in same timezone as event, just show the time once
    const fullDisplay = userTimezone === eventTimezone 
      ? userTime 
      : `${userTime} (${eventTimeStr} ${eventTZAbbr})`;
    
    return {
      date: correctDate,
      displayTime: userTime,
      eventTimeDisplay: `${eventTimeStr} ${eventTZAbbr}`,
      fullDisplay: fullDisplay
    };
  } catch (error) {
    console.error('Error converting event time:', error);
    // Fallback: at least format to 12-hour even if timezone conversion fails.
    // The date must still be a REAL date. It used to be `new Date(date + 'T' + time)`, which is
    // Invalid Date for anything that isn't HH:MM — and an invalid date thrown into the month grid
    // takes the whole Calendar page down with a RangeError (found Jun 2026). Midday on the event's
    // own date keeps one bad record to one odd-looking entry.
    const formattedTime = formatTimeTo12Hour(eventTime);
    let date = new Date(`${eventDate}T${eventTime}`);
    if (isNaN(date.getTime())) {
      date = new Date(`${eventDate}T12:00:00`);
    }
    if (isNaN(date.getTime())) {
      date = new Date();
    }
    return {
      date,
      displayTime: formattedTime,
      eventTimeDisplay: formattedTime,
      fullDisplay: formattedTime
    };
  }
};

/**
 * Get timezone offset in milliseconds
 * @param {string} timezone - IANA timezone string
 * @param {Date} date - Date to get offset for
 * @returns {number} Offset in milliseconds
 */
const getTimezoneOffset = (timezone, date) => {
  try {
    // Get the date string in the specified timezone
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    // Get the date string in UTC
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    // Return the difference
    return tzDate.getTime() - utcDate.getTime();
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return 0;
  }
};

/**
 * Sort events by time chronologically
 * @param {Array} events - Array of event objects with event_date and event_time
 * @returns {Array} Sorted array of events
 */
export const sortEventsByTime = (events) => {
  return [...events].sort((a, b) => {
    // Combine date and time for comparison
    const dateTimeA = new Date(`${a.event_date}T${a.event_time}`);
    const dateTimeB = new Date(`${b.event_date}T${b.event_time}`);
    return dateTimeA - dateTimeB;
  });
};
